import {
  createAction,
  createAsyncThunk,
  createSlice,
  isAnyOf,
} from "@reduxjs/toolkit";

import { del, get, post, put, uploadFile } from "../../Services/ApiRequest";
import type {
  AccountPlanOverview,
  AccountPlanStatus,
  ChatContext,
  ChatMessage,
  ChatSuggestions,
  DraftPendingRefusal,
  PendingDraft,
  PlanActivity,
  PlanActivityFinished,
  PlanActivityMine,
  PlanActivityMineItem,
  PlanChat,
  PlanFile,
  PlanLink,
  PlanRejectedUpload,
  PortfolioRoles,
  SectionOwner,
  UploadRejection,
} from "../../Types/AccountPlan";
import { API_URLS } from "../../Utils/Constants/APIConstants";

/** The only place Account Plans talks to the API (team rule: thunks in *Slice.ts). */
const unwrap = (res: any) => res?.content ?? res;

/** A loadable thing, keyed (by account, by account:file, by account list). */
export interface Loadable<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  fetchedAt: number;
  inFlight: boolean;
}

const empty = <T>(): Loadable<T> => ({
  data: null,
  loading: true,
  error: null,
  fetchedAt: 0,
  inFlight: false,
});

/** Several components watch the same plan; within this gap they share one request. */
export const MIN_REFETCH_MS = 1500;

/** One account of the organization, for Plans → All accounts. */
export interface OrgAccountRow {
  customer_id: string;
  customer_name: string;
  industry: string | null;
  revenue: number | null;
  renewal_date: string | null;
  tier: string | null;
}

export interface StatusesPayload {
  accounts: Record<string, AccountPlanStatus>;
  me: PortfolioRoles | null;
  /** scope "all" only: every active account in the organization. */
  rows?: OrgAccountRow[];
}

/** Every version of one account's plan (the Plans list's account panel). */
export interface PlanVersions {
  files: PlanFile[];
  current_file_id: string | null;
  sponsor_file_id: string | null;
  /** Whether the plan page itself lets this user in. */
  can_open: boolean;
  /** Whether this user can read the published briefing (anyone in the org, once published). */
  can_read_published?: boolean;
  /** Pre-parse rejections (Phase 3), when the endpoint sends them. */
  rejected_uploads?: PlanRejectedUpload[];
  /** A confirmed, unpublished draft that blocks a new upload (2026-10-30). */
  pending_draft?: PendingDraft | null;
}

/** What a reset removed (backend: AccountPlanService.reset_account). */
export interface AccountPlanResetResult {
  customer_id: string;
  deleted: Record<string, number>;
  storage_cleaned: boolean;
}

interface AccountPlanState {
  /** Is Account Plans on for this organization? null until known. */
  enabled: boolean | null;
  /** Plan chat has its own switch (out of v1 scope); false until known. */
  chatEnabled: boolean;
  /** Reset is demo-only (server ACCOUNT_PLANS_DEMO_RESET_ENABLED); false until known. */
  demoReset: boolean;
  /** key = sorted account ids → List-tab status payload. */
  statuses: Record<string, Loadable<StatusesPayload>>;
  /** key = accountId → every version (metadata only), readable organization-wide. */
  versions: Record<string, Loadable<PlanVersions>>;
  /** key = accountId → plan page overview. */
  overview: Record<string, Loadable<AccountPlanOverview>>;
  /** key = `${accountId}:${fileId}` → one version with parsed pages. Keyed so a
   * late reply for a version you navigated away from can't overwrite this one. */
  files: Record<string, Loadable<PlanFile>>;
  /** customer_id → a reset is in flight. */
  resetting: Record<string, boolean>;
  /** customer_id → why the last reset failed. */
  resetError: Record<string, string>;
  /** Team ledger, by scope ("mine" | "all"). */
  activity: Record<string, Loadable<PlanActivity>>;
  /** My plans in line or processing (header chip). */
  myActive: PlanActivityMineItem[];
  /** Server time of the last "mine" poll: outcomes after it become pop-ups. */
  mySince: string | null;
  /** Outcomes not yet shown or dismissed (pop-ups), oldest first. */
  toasts: PlanActivityFinished[];
  /** Versions whose outcome the person already saw in the upload dialog: no pop-up. */
  seenOutcomes: Record<string, boolean>;
  /** accountId → bytes of the plan file sent so far, while it is still uploading. */
  uploadProgress: Record<string, UploadProgress>;
}

export interface UploadProgress {
  sent: number;
  total: number;
}

const initialState: AccountPlanState = {
  enabled: null,
  chatEnabled: false,
  demoReset: false,
  statuses: {},
  versions: {},
  overview: {},
  files: {},
  resetting: {},
  resetError: {},
  activity: {},
  myActive: [],
  mySince: null,
  toasts: [],
  seenOutcomes: {},
  uploadProgress: {},
};

/**
 * Friendly errors (decision 2026-10-19): an Account Plans screen never shows a raw
 * infrastructure or server error — no status codes, stack text, exception names or
 * a 5xx body. Every AP error reaches the UI through `errorMessage` (and
 * `apiErrorMessage`, its alias in Hooks/useAccountPlan), which maps it by situation:
 *
 * - no response / network / request setup, and any 5xx → ACCOUNT_PLANS_UNAVAILABLE
 * - 401 → SESSION_EXPIRED; 404 → PLAN_NOT_AVAILABLE (also hides whether it exists),
 *   except the switched-off 404, ACCOUNT_PLANS_OFF, which names no plan
 * - 400, 403, 409, 413, 422, 429 → the server's own message, but only when it is
 *   one of the deliberate sentences AP routes write for users (HTTPException
 *   `detail` strings such as "Only the AE on this account can do that." or "A plan
 *   version is still being processed. …"). A message counts as deliberate only if
 *   it is a short plain sentence (see `isPlainSentence`); FastAPI's validation
 *   list, HTML from a proxy, or anything that looks like code falls back to the
 *   status's own wording (403 → NO_ACCESS, 413, 429) or the caller's `fallback`.
 * - an error with no status at all was built by our own code, not received from
 *   a server: its message is shown if it is a plain sentence.
 * - anything else (other 4xx, non-JSON errors) → the caller's `fallback`, which
 *   names the action that failed ("Couldn't publish the briefing.").
 */
export const ACCOUNT_PLANS_UNAVAILABLE =
  "Account Plans is temporarily unavailable. Try again in a few minutes.";
export const PLAN_NOT_AVAILABLE = "This plan isn't available.";
/** The server's own 404 while the organization's Account Plans switch is off (FEAT-01):
 * it names the organization's setting, not any plan, so it is shown as is. */
export const ACCOUNT_PLANS_OFF =
  "Account Plans isn't enabled for this organization.";
export const NO_ACCESS = "You don't have access to this plan.";
export const SESSION_EXPIRED = "Your session has expired. Sign in again.";
const TOO_MANY = "Too many requests right now. Try again in a few minutes.";
const TOO_LARGE = "The file is too large.";

/** Statuses for which AP routes answer with a sentence written for the user. */
const OWN_MESSAGE_STATUSES = [400, 403, 409, 413, 422, 429];

/** Looks like code, markup or an internal error rather than a sentence for a user. */
const TECHNICAL =
  /[<>{}[\]\\|`]|\n|traceback|exception|error:|errno|stack|sqlalchemy|asyncpg|psycopg|pydantic|internal server|bad gateway|gateway|upstream|nginx|\.py\b|\bnull\b|\bundefined\b|status code/i;

/** A short, plain, user-facing sentence (not a list, not markup, not code). */
export const isPlainSentence = (message: unknown): message is string =>
  typeof message === "string" &&
  message.trim().length > 0 &&
  message.length <= 300 &&
  !TECHNICAL.test(message);

/** ApiRequest rejects with Error(JSON.stringify({status, message, …})). */
const parseApiError = (
  error: any
): { status?: number | string; message?: unknown } | null => {
  try {
    const parsed = JSON.parse(error?.message || "");
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
};

/** A thunk's error as friendly, readable text — never an object or a raw server string. */
export const errorMessage = (error: any, fallback: string): string => {
  const parsed = parseApiError(error);
  if (!parsed) return fallback;
  const { status, message } = parsed;
  if (status === undefined || status === null)
    return isPlainSentence(message) ? message.trim() : fallback;
  // A non-numeric status (ApiRequest's NO_RESPONSE / REQUEST_SETUP_ERROR): no
  // response, a request that couldn't be sent, or a network failure.
  if (typeof status !== "number") return ACCOUNT_PLANS_UNAVAILABLE;
  if (status >= 500) return ACCOUNT_PLANS_UNAVAILABLE;
  if (status === 401) return SESSION_EXPIRED;
  if (status === 404)
    return message === ACCOUNT_PLANS_OFF
      ? ACCOUNT_PLANS_OFF
      : PLAN_NOT_AVAILABLE;
  if (OWN_MESSAGE_STATUSES.includes(status) && isPlainSentence(message))
    return message.trim();
  if (status === 403) return NO_ACCESS;
  if (status === 429) return TOO_MANY;
  if (status === 413) return TOO_LARGE;
  return fallback;
};

/** Per-tenant switches (vendor_config "account_plans"): the feature, plan chat
 * on its own, and the demo-only Reset (a server setting, off in production). Any
 * failure reads as off. */
export const fetchAccountPlansEnabled = createAsyncThunk<{
  enabled: boolean;
  chat: boolean;
  demoReset: boolean;
}>("accountPlan/enabled", async () => {
  try {
    const res = await get(API_URLS.accountPlansEnabled());
    const flags = res?.content ?? res;
    const enabled = flags?.enabled === true;
    return {
      enabled,
      chat: enabled && flags?.chat === true,
      demoReset: enabled && flags?.demo_reset === true,
    };
  } catch {
    return { enabled: false, chat: false, demoReset: false };
  }
});

type SliceState = { accountPlan: AccountPlanState };

/** Skip a request when the same key is already loading or was just fetched. */
const fresh = (entry: Loadable<unknown> | undefined, force?: boolean) =>
  !entry ||
  (!entry.inFlight && (force || Date.now() - entry.fetchedAt > MIN_REFETCH_MS));

export const fileKey = (accountId: string, fileId: string) =>
  `${accountId}:${fileId}`;
export const statusesKey = (ids: string[]) => ids.slice().sort().join(",");
/** Plans → All accounts: every account in the organization, one entry. */
export const ALL_ACCOUNTS_KEY = "__all__";
const keyOf = (arg: { ids: string[]; scope?: "mine" | "all" }) =>
  arg.scope === "all" ? ALL_ACCOUNTS_KEY : statusesKey(arg.ids);

export const fetchPlanStatuses = createAsyncThunk<
  StatusesPayload,
  { ids: string[]; force?: boolean; scope?: "mine" | "all" },
  { state: SliceState; rejectValue: string }
>(
  "accountPlan/statuses",
  async ({ ids, scope }, { rejectWithValue }) => {
    try {
      const res = unwrap(
        await post(
          API_URLS.accountPlanStatus(),
          scope === "all"
            ? { account_ids: [], scope: "all" }
            : { account_ids: ids }
        )
      );
      return {
        accounts: res?.accounts || {},
        me: res?.me || null,
        ...(res?.rows ? { rows: res.rows } : {}),
      };
    } catch (error: any) {
      return rejectWithValue(errorMessage(error, "Couldn't load plan status."));
    }
  },
  {
    condition: (arg, { getState }) =>
      fresh(getState().accountPlan.statuses[keyOf(arg)], arg.force),
  }
);

export const fetchPlanVersions = createAsyncThunk<
  PlanVersions,
  { accountId: string; force?: boolean },
  { state: SliceState; rejectValue: string }
>(
  "accountPlan/versions",
  async ({ accountId }, { rejectWithValue }) => {
    try {
      return unwrap(
        await get(`${API_URLS.accountPlanOverview(accountId)}/versions`)
      );
    } catch (error: any) {
      return rejectWithValue(
        errorMessage(error, "Couldn't load the version history.")
      );
    }
  },
  {
    condition: ({ accountId, force }, { getState }) =>
      fresh(getState().accountPlan.versions[accountId], force),
  }
);

export const fetchPlanOverview = createAsyncThunk<
  AccountPlanOverview,
  { accountId: string; force?: boolean },
  { state: SliceState; rejectValue: string }
>(
  "accountPlan/overview",
  async ({ accountId }, { rejectWithValue }) => {
    try {
      return unwrap(await get(API_URLS.accountPlanOverview(accountId)));
    } catch (error: any) {
      return rejectWithValue(
        errorMessage(error, "Couldn't load this account plan.")
      );
    }
  },
  {
    condition: ({ accountId, force }, { getState }) =>
      fresh(getState().accountPlan.overview[accountId], force),
  }
);

/** A fresh link for the original deck, made (and logged) per click: links last
 * minutes, so the one loaded with the page may have expired. */
export const fetchPlanDownloadLink = createAsyncThunk<
  string,
  { accountId: string; fileId: string },
  { rejectValue: string }
>(
  "accountPlan/fileDownload",
  async ({ accountId, fileId }, { rejectWithValue }) => {
    try {
      const out = unwrap(
        await get(API_URLS.accountPlanFileDownload(accountId, fileId))
      );
      if (!out?.url)
        return rejectWithValue("This file isn't available to download.");
      return out.url as string;
    } catch (error: any) {
      return rejectWithValue(
        errorMessage(error, "Couldn't download the original file.")
      );
    }
  }
);

export const fetchPlanFile = createAsyncThunk<
  PlanFile,
  { accountId: string; fileId: string; force?: boolean },
  { state: SliceState; rejectValue: string }
>(
  "accountPlan/file",
  async ({ accountId, fileId }, { rejectWithValue }) => {
    try {
      return unwrap(await get(API_URLS.accountPlanFile(accountId, fileId)));
    } catch (error: any) {
      return rejectWithValue(
        errorMessage(error, "Couldn't load this plan version.")
      );
    }
  },
  {
    condition: ({ accountId, fileId, force }, { getState }) =>
      fresh(getState().accountPlan.files[fileKey(accountId, fileId)], force),
  }
);

// Mutations reject with the request's own error (not rejectWithValue), so
// `dispatch(x).unwrap()` throws an object whose `message` callers already parse.
const reviewUrl = (accountId: string, fileId: string, action: string) =>
  `${API_URLS.accountPlanFile(accountId, fileId)}/review/${action}`;

const MB = 1024 * 1024;

/** Bytes of an account's plan file sent so far (from uploadPlanFileThunk). */
export const setUploadProgress = createAction<
  { accountId: string } & UploadProgress
>("accountPlan/uploadProgress");

/**
 * Upload a plan file. `moveFrom` (upload flow Phase 3): the other account's copy
 * of this exact file; the server discards that copy and takes this upload
 * ("Move it here from {account}" on a duplicate_other rejection).
 * `replaceDraft` (2026-10-30): the AE chose "Replace this draft"; without it the
 * server refuses an upload while a confirmed draft is unpublished (409
 * draft_pending), and with it that draft is set aside once this upload is confirmed.
 */
export const uploadPlanFileThunk = createAsyncThunk(
  "accountPlan/upload",
  async (
    {
      accountId,
      file,
      moveFrom,
      replaceDraft,
    }: {
      accountId: string;
      file: File;
      moveFrom?: string | null;
      replaceDraft?: boolean;
    },
    { dispatch }
  ) => {
    const fields: Record<string, string> = {};
    if (moveFrom) fields.move_from = moveFrom;
    if (replaceDraft) fields.replace_draft = "true";
    // Bytes sent so far, for "Uploading … 42 of 150 MB" (Sushant, 2026-11-04: a
    // 150 MB deck sat on a bare spinner for a minute). Only whole-MB steps are
    // stored, so a big upload doesn't re-render on every progress event.
    let lastMb = -1;
    const onUploadProgress = (e: { loaded: number; total?: number }) => {
      const total = e.total || file.size;
      const mb = Math.floor(e.loaded / MB);
      if (mb === lastMb && e.loaded < total) return;
      lastMb = mb;
      dispatch(setUploadProgress({ accountId, sent: e.loaded, total }));
    };
    return unwrap(
      await uploadFile(
        API_URLS.accountPlanFiles(accountId),
        file,
        { onUploadProgress },
        Object.keys(fields).length ? fields : undefined
      )
    ) as PlanFile;
  }
);

/**
 * The 409 draft_pending refusal (a tab that still offered the drop zone after a
 * draft was confirmed), or null for any other error.
 */
export const draftPendingOf = (error: any): DraftPendingRefusal | null => {
  const parsed = parseApiError(error) as {
    status?: number | string;
    message?: unknown;
    code?: unknown;
    content?: any;
  } | null;
  if (!parsed || parsed.status !== 409) return null;
  const body =
    parsed.content && typeof parsed.content === "object"
      ? parsed.content
      : parsed.message && typeof parsed.message === "object"
        ? (parsed.message as any)
        : null;
  if ((body?.code ?? parsed.code) !== "draft_pending") return null;
  const message = isPlainSentence(body?.message)
    ? body.message.trim()
    : isPlainSentence(parsed.message)
      ? (parsed.message as string).trim()
      : "A draft is in review. Publish it, or replace it, before uploading a new plan.";
  const pd = body?.other?.pending_draft;
  return {
    code: "draft_pending",
    message,
    pending_draft:
      pd && typeof pd === "object" && pd.file_id ? (pd as PendingDraft) : null,
  };
};

/** Dismiss the account's pre-parse rejections (upload flow Phase 3). */
export const dismissPlanRejectionsThunk = createAsyncThunk(
  "accountPlan/dismissRejections",
  async ({ accountId }: { accountId: string }) =>
    unwrap(
      await post(
        `${API_URLS.accountPlanOverview(accountId)}/rejections/dismiss`,
        {}
      )
    )
);

/**
 * An upload's error as a pre-parse rejection, or null when it is some other
 * failure (network, 5xx, "still being processed", no access…), which the upload
 * area keeps showing as a plain error line.
 *
 * - 409 with code duplicate_other / duplicate_here: the exact-duplicate refusal;
 *   its body arrives as `content` (the envelope) or as `detail` (a bare
 *   HTTPException), so both are read.
 * - 400 / 413: any other refusal before reading (type, password, damaged, pages…),
 *   worded by `errorMessage` so a raw server string never reaches the screen.
 */
export const uploadRejectionOf = (error: any): UploadRejection | null => {
  const parsed = parseApiError(error) as {
    status?: number | string;
    message?: unknown;
    code?: unknown;
    content?: any;
  } | null;
  if (!parsed) return null;
  const { status } = parsed;
  if (status === 409) {
    const body =
      parsed.content && typeof parsed.content === "object"
        ? parsed.content
        : parsed.message && typeof parsed.message === "object"
          ? (parsed.message as any)
          : null;
    const code = body?.code ?? parsed.code;
    if (code !== "duplicate_other" && code !== "duplicate_here") return null;
    const text = isPlainSentence(body?.message)
      ? body.message.trim()
      : isPlainSentence(parsed.message)
        ? (parsed.message as string).trim()
        : code === "duplicate_other"
          ? "This exact file is already another account's plan."
          : "This exact file is already uploaded here.";
    const other = body?.other || {};
    if (code === "duplicate_other")
      return {
        code,
        message: text,
        other: {
          account_id: other.account_id ?? null,
          account_name: other.account_name ?? null,
          version: Number(other.version) || 0,
          plan_version:
            other.plan_version == null ? null : Number(other.plan_version),
          uploaded_by_name: other.uploaded_by_name ?? null,
          uploaded_by_you: !!other.uploaded_by_you,
          uploaded_at: other.uploaded_at ?? null,
          published: !!other.published,
          movable: !!other.movable,
          file_id: other.file_id ?? other.id ?? null,
        },
      };
    return {
      code,
      message: text,
      other: {
        file_id: other.file_id ?? null,
        version: Number(other.version) || 0,
        plan_version:
          other.plan_version == null ? null : Number(other.plan_version),
        status: String(other.status ?? ""),
        uploaded_at: other.uploaded_at ?? null,
      },
    };
  }
  if (status === 400 || status === 413)
    return {
      code: "other",
      message: errorMessage(error, "This file can't be used as a plan."),
      other: null,
    };
  return null;
};

/** Take back an upload still waiting in line (spec QUE-05). */
export const withdrawPlanUploadThunk = createAsyncThunk(
  "accountPlan/withdraw",
  async ({ accountId, fileId }: { accountId: string; fileId: string }) =>
    unwrap(
      await post(`${API_URLS.accountPlanFile(accountId, fileId)}/withdraw`, {})
    )
);

/** Read a version that failed again, from the stored file (spec QUE-07). */
export const retryPlanUploadThunk = createAsyncThunk(
  "accountPlan/retry",
  async ({ accountId, fileId }: { accountId: string; fileId: string }) =>
    unwrap(
      await post(`${API_URLS.accountPlanFile(accountId, fileId)}/retry`, {})
    ) as PlanFile
);

/** Delete one version ("Not this plan" on a flagged or rejected upload). */
export const deletePlanVersionThunk = createAsyncThunk(
  "accountPlan/deleteVersion",
  async ({ accountId, fileId }: { accountId: string; fileId: string }) =>
    unwrap(await del(API_URLS.accountPlanFile(accountId, fileId)))
);

/** Delete, now, an upload that didn't become a plan (AE; else deleted after N days). */
export const deleteUnusedUploadThunk = createAsyncThunk(
  "accountPlan/deleteUnused",
  async ({ accountId, fileId }: { accountId: string; fileId: string }) =>
    unwrap(
      await del(`${API_URLS.accountPlanOverview(accountId)}/unused/${fileId}`)
    )
);

/** Delete one refused upload's notice (nothing was stored; only the notice goes). */
export const deleteRejectionThunk = createAsyncThunk(
  "accountPlan/deleteRejection",
  async ({
    accountId,
    rejectionId,
  }: {
    accountId: string;
    rejectionId: string;
  }) =>
    unwrap(
      await del(
        `${API_URLS.accountPlanOverview(accountId)}/rejections/${rejectionId}`
      )
    )
);

/**
 * Confirm plan (upload flow Phase 2; spec VAL-22): the AE accepts a read version,
 * clean or flagged, as the account's plan. The server records it and starts the
 * draft briefing and Briefing Questions; the response is the version with
 * sponsor_view_status "processing".
 */
export const confirmPlanVersionThunk = createAsyncThunk(
  "accountPlan/confirmVersion",
  async ({
    accountId,
    fileId,
    note,
    acknowledged,
  }: {
    accountId: string;
    fileId: string;
    /** The AE's optional note on the plan, kept with the version. */
    note?: string;
    /** The warnings the AE ticked: the server refuses the confirm unless all are. */
    acknowledged?: string[];
  }) =>
    unwrap(
      await post(`${API_URLS.accountPlanFile(accountId, fileId)}/confirm`, {
        ...(note?.trim() ? { note: note.trim() } : {}),
        ...(acknowledged?.length ? { acknowledged } : {}),
      })
    ) as PlanFile
);

/** "This is the right plan" on a rejected version, with a reason (spec VAL-23). */
export const overridePlanVersionThunk = createAsyncThunk(
  "accountPlan/overrideVersion",
  async ({
    accountId,
    fileId,
    reason,
  }: {
    accountId: string;
    fileId: string;
    reason: string;
  }) =>
    unwrap(
      await post(`${API_URLS.accountPlanFile(accountId, fileId)}/override`, {
        reason,
      })
    ) as PlanFile
);

/** "Not this file" on Confirm plan: discard the newest unconfirmed upload (undo for 7 days). */
export const discardPlanVersionThunk = createAsyncThunk(
  "accountPlan/discardVersion",
  async ({ accountId, fileId }: { accountId: string; fileId: string }) =>
    unwrap(
      await post(`${API_URLS.accountPlanFile(accountId, fileId)}/discard`, {})
    ) as PlanFile
);

/** Undo "Not this file": the upload goes back to waiting for Confirm plan. */
export const restorePlanVersionThunk = createAsyncThunk(
  "accountPlan/restoreVersion",
  async ({ accountId, fileId }: { accountId: string; fileId: string }) =>
    unwrap(
      await post(`${API_URLS.accountPlanFile(accountId, fileId)}/restore`, {})
    ) as PlanFile
);

/** Team ledger (spec ACT-02). */
export const fetchPlanActivity = createAsyncThunk<
  PlanActivity,
  { scope: "mine" | "all"; force?: boolean },
  { state: SliceState; rejectValue: string }
>(
  "accountPlan/activity",
  async ({ scope }, { rejectWithValue }) => {
    try {
      return unwrap(await get(API_URLS.accountPlanActivity(scope)));
    } catch (error: any) {
      return rejectWithValue(
        errorMessage(error, "Couldn't load plan activity.")
      );
    }
  },
  {
    condition: ({ scope, force }, { getState }) =>
      fresh(getState().accountPlan.activity[scope], force),
  }
);

/** My plans in flight + outcomes since the last poll (header chip, pop-ups; NTF-05). */
export const fetchMyPlanActivity = createAsyncThunk<
  PlanActivityMine,
  void,
  { state: SliceState; rejectValue: string }
>("accountPlan/activityMine", async (_, { getState, rejectWithValue }) => {
  try {
    const since = getState().accountPlan.mySince;
    return unwrap(await get(API_URLS.accountPlanActivityMine(since)));
  } catch (error: any) {
    return rejectWithValue(errorMessage(error, "Couldn't load plan activity."));
  }
});

export const addPlanLinkThunk = createAsyncThunk(
  "accountPlan/addLink",
  async ({ accountId, url }: { accountId: string; url: string }) =>
    unwrap(
      await post(API_URLS.accountPlanLinks(accountId), { url })
    ) as PlanLink
);

export const deletePlanLinkThunk = createAsyncThunk(
  "accountPlan/deleteLink",
  async ({ accountId, linkId }: { accountId: string; linkId: string }) =>
    unwrap(await del(API_URLS.accountPlanLink(accountId, linkId)))
);

export const markPlanLinkOpenedThunk = createAsyncThunk(
  "accountPlan/linkOpened",
  async ({ accountId, linkId }: { accountId: string; linkId: string }) =>
    unwrap(
      await post(`${API_URLS.accountPlanLink(accountId, linkId)}/opened`, {})
    )
);

export const regenerateSponsorViewThunk = createAsyncThunk(
  "accountPlan/regenerate",
  async ({ accountId, fileId }: { accountId: string; fileId: string }) =>
    unwrap(
      await post(API_URLS.accountPlanSponsorView(accountId, fileId), {})
    ) as PlanFile
);

export const setSectionOwnerThunk = createAsyncThunk(
  "accountPlan/sectionOwner",
  async ({
    accountId,
    sectionId,
    role,
  }: {
    accountId: string;
    sectionId: string;
    role: SectionOwner;
  }) =>
    unwrap(
      await put(
        `${API_URLS.accountPlanOverview(accountId)}/section-owners/${encodeURIComponent(sectionId)}`,
        { role }
      )
    )
);

export const verifySectionsThunk = createAsyncThunk(
  "accountPlan/verify",
  async ({
    accountId,
    fileId,
    sectionIds,
    role,
  }: {
    accountId: string;
    fileId: string;
    sectionIds: string[];
    role: SectionOwner;
  }) =>
    unwrap(
      await post(reviewUrl(accountId, fileId, "verify"), {
        section_ids: sectionIds,
        role,
      })
    ) as PlanFile
);

export const addSectionOverlayThunk = createAsyncThunk(
  "accountPlan/overlay",
  async ({
    accountId,
    fileId,
    sectionId,
    text,
    role,
  }: {
    accountId: string;
    fileId: string;
    sectionId: string;
    text: string;
    role: SectionOwner;
  }) =>
    unwrap(
      await post(reviewUrl(accountId, fileId, "overlay"), {
        section_id: sectionId,
        text,
        role,
      })
    ) as PlanFile
);

export const acknowledgePlanGapsThunk = createAsyncThunk(
  "accountPlan/gaps",
  async ({
    accountId,
    fileId,
    acknowledged,
  }: {
    accountId: string;
    fileId: string;
    acknowledged: boolean;
  }) =>
    unwrap(
      await post(reviewUrl(accountId, fileId, "gaps"), { acknowledged })
    ) as PlanFile
);

/** AE: confirm the sponsor will see no briefing-question answers (spec PUB-03). */
export const acknowledgeNoAnswersThunk = createAsyncThunk(
  "accountPlan/noAnswers",
  async ({
    accountId,
    fileId,
    acknowledged,
  }: {
    accountId: string;
    fileId: string;
    acknowledged: boolean;
  }) =>
    unwrap(
      await post(reviewUrl(accountId, fileId, "no-answers"), { acknowledged })
    ) as PlanFile
);

export const publishBriefingThunk = createAsyncThunk(
  "accountPlan/publish",
  async ({ accountId, fileId }: { accountId: string; fileId: string }) =>
    unwrap(await post(reviewUrl(accountId, fileId, "publish"), {})) as PlanFile
);

/** Admin only: wipe every plan version, chat, link and stored file for one account. */
// --- Plan chat --------------------------------------------------------------------------
// Like the mutations above, these reject with the request's own error: the chat hook
// reads its status and code (rate limited, answer failed, no plan) from it.

export const fetchPlanChatSuggestionsThunk = createAsyncThunk(
  "accountPlan/chatSuggestions",
  async ({ accountId, fileId }: { accountId: string; fileId: string | null }) =>
    unwrap(
      await get(API_URLS.accountPlanChatSuggestions(accountId, fileId))
    ) as ChatSuggestions
);

export const fetchPlanChatsThunk = createAsyncThunk(
  "accountPlan/chats",
  async ({ accountId }: { accountId: string }) =>
    (unwrap(await get(API_URLS.accountPlanChats(accountId))) ||
      []) as PlanChat[]
);

export const fetchPlanChatMessagesThunk = createAsyncThunk(
  "accountPlan/chatMessages",
  async ({ accountId, chatId }: { accountId: string; chatId: string }) =>
    (unwrap(await get(API_URLS.accountPlanChatMessages(accountId, chatId))) ||
      []) as ChatMessage[]
);

export const createPlanChatThunk = createAsyncThunk(
  "accountPlan/createChat",
  async ({ accountId, fileId }: { accountId: string; fileId: string | null }) =>
    unwrap(
      await post(
        API_URLS.accountPlanChats(accountId),
        fileId ? { file_id: fileId } : {}
      )
    ) as PlanChat
);

export const askPlanChatThunk = createAsyncThunk(
  "accountPlan/askChat",
  async ({
    accountId,
    chatId,
    question,
    questionId,
    context,
  }: {
    accountId: string;
    chatId: string;
    question: string;
    questionId?: string | null;
    context?: ChatContext | null;
  }) => {
    const body: Record<string, unknown> = { question };
    if (questionId) body.question_id = questionId;
    if (context) body.context = context;
    return unwrap(
      await post(API_URLS.accountPlanChatMessages(accountId, chatId), body)
    ) as { question: ChatMessage; answer: ChatMessage };
  }
);

export const planChatFeedbackThunk = createAsyncThunk(
  "accountPlan/chatFeedback",
  async ({
    accountId,
    chatId,
    messageId,
    rating,
  }: {
    accountId: string;
    chatId: string;
    messageId: string;
    rating: 1 | -1;
  }) =>
    unwrap(
      await post(
        API_URLS.accountPlanChatFeedback(accountId, chatId, messageId),
        { rating }
      )
    ) as ChatMessage | null
);

export const resetAccountPlan = createAsyncThunk<
  AccountPlanResetResult,
  string,
  { rejectValue: string }
>("accountPlan/reset", async (customerId, { rejectWithValue }) => {
  try {
    const res = await del(API_URLS.accountPlanOverview(customerId));
    return (res?.content ?? res) as AccountPlanResetResult;
  } catch (error: any) {
    return rejectWithValue(
      errorMessage(error, "Couldn't reset this account's plans.")
    );
  }
});

const AccountPlanSlice = createSlice({
  name: "accountPlan",
  initialState,
  reducers: {
    clearResetError: (state, action: { payload: string }) => {
      delete state.resetError[action.payload];
    },
    /** The person saw this version's outcome in a dialog ("read": the upload dialog,
     * "brief": the briefing dialog): no pop-up for it. */
    markPlanOutcomeSeen: (
      state,
      action: { payload: { fileId: string; kind: "read" | "brief" } }
    ) => {
      const { fileId, kind } = action.payload;
      state.seenOutcomes[`${fileId}:${kind}`] = true;
      state.toasts = state.toasts.filter(
        t =>
          t.file_id !== fileId ||
          t.event.startsWith("briefing") !== (kind === "brief")
      );
    },
    dismissPlanToast: (
      state,
      action: { payload: { fileId: string; event: string } }
    ) => {
      state.toasts = state.toasts.filter(
        t =>
          !(
            t.file_id === action.payload.fileId &&
            t.event === action.payload.event
          )
      );
    },
  },
  extraReducers: builder => {
    builder
      .addCase(setUploadProgress, (state, { payload }) => {
        state.uploadProgress[payload.accountId] = {
          sent: payload.sent,
          total: payload.total,
        };
      })
      .addCase(uploadPlanFileThunk.fulfilled, (state, { meta }) => {
        delete state.uploadProgress[meta.arg.accountId];
      })
      .addCase(uploadPlanFileThunk.rejected, (state, { meta }) => {
        delete state.uploadProgress[meta.arg.accountId];
      });
    const loadable = <T>(
      map: Record<string, Loadable<T>>,
      key: string,
      phase: "pending" | "fulfilled" | "rejected",
      payload?: any
    ) => {
      const e = (map[key] = map[key] || (empty<T>() as Loadable<T>));
      if (phase === "pending") {
        e.inFlight = true;
        if (!e.data) e.loading = true;
      } else if (phase === "fulfilled") {
        Object.assign(e, {
          data: payload,
          loading: false,
          error: null,
          inFlight: false,
          fetchedAt: Date.now(),
        });
      } else {
        // Keep what's already shown: one failed refresh must not blank the page.
        Object.assign(e, {
          loading: false,
          error: payload || "Something went wrong.",
          inFlight: false,
          fetchedAt: Date.now(),
        });
      }
    };
    builder
      .addCase(fetchPlanStatuses.pending, (s, a) =>
        loadable(s.statuses, keyOf(a.meta.arg), "pending")
      )
      .addCase(fetchPlanStatuses.fulfilled, (s, a) =>
        loadable(s.statuses, keyOf(a.meta.arg), "fulfilled", a.payload)
      )
      .addCase(fetchPlanStatuses.rejected, (s, a) =>
        loadable(s.statuses, keyOf(a.meta.arg), "rejected", a.payload)
      )
      .addCase(fetchPlanVersions.pending, (s, a) =>
        loadable(s.versions, a.meta.arg.accountId, "pending")
      )
      .addCase(fetchPlanVersions.fulfilled, (s, a) =>
        loadable(s.versions, a.meta.arg.accountId, "fulfilled", a.payload)
      )
      .addCase(fetchPlanVersions.rejected, (s, a) =>
        loadable(s.versions, a.meta.arg.accountId, "rejected", a.payload)
      )
      .addCase(fetchPlanOverview.pending, (s, a) =>
        loadable(s.overview, a.meta.arg.accountId, "pending")
      )
      .addCase(fetchPlanOverview.fulfilled, (s, a) =>
        loadable(s.overview, a.meta.arg.accountId, "fulfilled", a.payload)
      )
      .addCase(fetchPlanOverview.rejected, (s, a) =>
        loadable(s.overview, a.meta.arg.accountId, "rejected", a.payload)
      )
      .addCase(fetchPlanFile.pending, (s, a) =>
        loadable(
          s.files,
          fileKey(a.meta.arg.accountId, a.meta.arg.fileId),
          "pending"
        )
      )
      .addCase(fetchPlanFile.fulfilled, (s, a) =>
        loadable(
          s.files,
          fileKey(a.meta.arg.accountId, a.meta.arg.fileId),
          "fulfilled",
          a.payload
        )
      )
      .addCase(fetchPlanFile.rejected, (s, a) =>
        loadable(
          s.files,
          fileKey(a.meta.arg.accountId, a.meta.arg.fileId),
          "rejected",
          a.payload
        )
      )
      .addCase(fetchPlanActivity.pending, (s, a) =>
        loadable(s.activity, a.meta.arg.scope, "pending")
      )
      .addCase(fetchPlanActivity.fulfilled, (s, a) =>
        loadable(s.activity, a.meta.arg.scope, "fulfilled", a.payload)
      )
      .addCase(fetchPlanActivity.rejected, (s, a) =>
        loadable(s.activity, a.meta.arg.scope, "rejected", a.payload)
      )
      .addCase(fetchMyPlanActivity.fulfilled, (state, action) => {
        state.myActive = action.payload.active || [];
        // The first poll only sets the clock: what finished before this page
        // loaded is in the bell, not a pop-up.
        if (state.mySince) {
          const seen = new Set(
            state.toasts.map(t => `${t.file_id}:${t.event}`)
          );
          (action.payload.finished || []).forEach(f => {
            const kind = f.event.startsWith("briefing") ? "brief" : "read";
            const key = `${f.file_id}:${f.event}`;
            // One pop-up per version and outcome (a retry that fails again doesn't stack
            // a second one), and none for what the person just watched in a dialog.
            if (!seen.has(key) && !state.seenOutcomes[`${f.file_id}:${kind}`]) {
              state.toasts.push(f);
              seen.add(key);
            }
          });
          state.toasts = state.toasts.slice(-3);
        }
        state.mySince = action.payload.server_time || state.mySince;
      })
      .addCase(fetchAccountPlansEnabled.fulfilled, (state, action) => {
        state.enabled = action.payload.enabled;
        state.chatEnabled = action.payload.chat;
        state.demoReset = action.payload.demoReset;
      })
      .addCase(resetAccountPlan.pending, (state, action) => {
        state.resetting[action.meta.arg] = true;
        delete state.resetError[action.meta.arg];
      })
      .addCase(resetAccountPlan.fulfilled, (state, action) => {
        const id = action.meta.arg;
        delete state.resetting[id];
        // Drop what we held for the wiped plans, so nothing on the open page
        // asks for a version that no longer exists (that 404 read as "not found").
        delete state.overview[id];
        Object.keys(state.files).forEach(k => {
          if (k.startsWith(`${id}:`)) delete state.files[k];
        });
      })
      .addCase(resetAccountPlan.rejected, (state, action) => {
        delete state.resetting[action.meta.arg];
        state.resetError[action.meta.arg] =
          action.payload || "Couldn't reset this account's plans.";
      })
      .addMatcher(
        isAnyOf(
          regenerateSponsorViewThunk.fulfilled,
          // Confirming (or "It's the right plan") starts the briefing too.
          confirmPlanVersionThunk.fulfilled,
          overridePlanVersionThunk.fulfilled
        ),
        (s, a) => {
          // The response is the version without its pages: take its status, so
          // the page polls the briefing without waiting for another read.
          const e = s.files[fileKey(a.meta.arg.accountId, a.meta.arg.fileId)];
          if (e?.data && a.payload) {
            e.data.sponsor_view_status = a.payload.sponsor_view_status;
            if (a.payload.status) e.data.status = a.payload.status;
            if (a.payload.confirmed !== undefined) {
              e.data.confirmed = a.payload.confirmed;
              e.data.confirmed_at = a.payload.confirmed_at;
              e.data.confirmed_by_name = a.payload.confirmed_by_name;
            }
            // F-UF-F15: confirming numbers the version (D1).
            if (a.payload.plan_version !== undefined)
              e.data.plan_version = a.payload.plan_version;
            if (a.payload.error !== undefined) e.data.error = a.payload.error;
          }
        }
      )
      .addMatcher(
        isAnyOf(
          discardPlanVersionThunk.fulfilled,
          restorePlanVersionThunk.fulfilled
        ),
        (s, a) => {
          // F-UF-F15: "Not this file" / Undo change the version's status and its
          // discard record; the cached copy follows without a forced refetch.
          const e = s.files[fileKey(a.meta.arg.accountId, a.meta.arg.fileId)];
          const p = a.payload as Partial<PlanFile> | undefined;
          if (e?.data && p) {
            if (p.status) e.data.status = p.status;
            if (p.error !== undefined) e.data.error = p.error;
            if (p.discarded !== undefined) e.data.discarded = p.discarded;
            if (p.confirmed !== undefined) e.data.confirmed = p.confirmed;
          }
        }
      );
  },
});

export const { clearResetError, dismissPlanToast, markPlanOutcomeSeen } =
  AccountPlanSlice.actions;
export default AccountPlanSlice.reducer;
